import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { FreshaLocation, FreshaEmployee, FreshaProvider } from '@/types/fresha';
import { useSettingsStore } from './settings.store.ts';
import { getAppMeta, setAppMeta } from '../lib/db/index.ts';

export interface CachedAuthMeta {
  locations: FreshaLocation[];
  employees: FreshaEmployee[];
  provider: FreshaProvider | null;
  workspaceId?: string | null;
}

export interface AuthUser {
  id: string;
  username: string;
  name: string;
  role: 'admin' | 'staff';
}

export interface AuthState {
  token: string | null;
  user: AuthUser | null;
  freshaConnected: boolean;
  freshaProvider: string | null;
  workspaceId: string | null;
  locationId: string | null;
  locations: FreshaLocation[];
  employees: FreshaEmployee[];
  provider: FreshaProvider | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  clientVersion: string;

  // Actions
  login: (username: string, password: string) => Promise<void>;
  loginWithSessionToken?: (token: string, email?: string) => Promise<void>;
  logout: () => void;
  initialize: () => Promise<void>;
  setLocationId: (id: string | null) => void;
  setToken: (token: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  setWorkspaceId: (workspaceId: string | null) => void;
  setLocations: (locations: FreshaLocation[]) => void;
  setEmployees: (employees: FreshaEmployee[]) => void;
  setProvider: (provider: FreshaProvider | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setClientVersion: (clientVersion: string) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        token: null,
        user: null,
        freshaConnected: false,
        freshaProvider: null,
        workspaceId: null,
        locationId: null,
        locations: [],
        employees: [],
        provider: null,
        isAuthenticated: false,
        isLoading: false,
        clientVersion: '2.8.6479',

        login: async (username: string, password: string) => {
          set({ isLoading: true });
          try {
            const res = await fetch('/api/auth/login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              body: JSON.stringify({ username, password }),
            });
            const result = await res.json().catch(() => ({}));
            if (!res.ok || !result.success) {
              throw new Error(result.error || 'Credenciales inválidas');
            }

            const { token, user } = result.data;
            set({
              token,
              user,
              isAuthenticated: true,
            });

            await get().initialize();
          } finally {
            set({ isLoading: false });
          }
        },

        loginWithSessionToken: async (token: string) => {
          set({ isLoading: true });
          try {
            set({
              token: token.trim(),
              isAuthenticated: true,
            });
            await get().initialize();
          } finally {
            set({ isLoading: false });
          }
        },

        logout: () => {
          set({
            token: null,
            user: null,
            freshaConnected: false,
            freshaProvider: null,
            workspaceId: null,
            locationId: null,
            locations: [],
            employees: [],
            provider: null,
            isAuthenticated: false,
            isLoading: false,
          });
          setAppMeta('auth_metadata', null).catch((e) =>
            console.error('Error clearing auth_metadata from Dexie:', e)
          );
        },

        initialize: async () => {
          // Hydrate cached metadata from Dexie immediately for offline readiness
          try {
            const cachedMeta = await getAppMeta<CachedAuthMeta>('auth_metadata');
            if (cachedMeta) {
              const currentLocId = get().locationId;
              const defaultLocId =
                currentLocId && cachedMeta.locations?.some((l) => l.id === currentLocId)
                  ? currentLocId
                  : cachedMeta.locations?.[0]?.id || null;

              set({
                locations: cachedMeta.locations || [],
                employees: cachedMeta.employees || [],
                provider: cachedMeta.provider || null,
                workspaceId: cachedMeta.workspaceId || get().workspaceId,
                locationId: currentLocId || defaultLocId,
              });
            }
          } catch (e) {
            console.error('Error hydrating auth_metadata from Dexie:', e);
          }

          const { token } = get();
          if (!token) {
            set({ isAuthenticated: false, isLoading: false });
            return;
          }
          set({ isLoading: true });
          try {
            // 1. Verify PWA user session
            const meRes = await fetch('/api/auth/me', {
              headers: { Authorization: `Bearer ${token}` },
            });
            const meResult = await meRes.json().catch(() => ({}));
            if (!meRes.ok || !meResult.success) {
              if (meRes.status === 401) {
                get().logout();
              }
              return;
            }

            const meData = meResult.data;
            set({
              user: meData.user || get().user,
              freshaConnected: Boolean(meData.freshaConnected),
              freshaProvider: meData.freshaProvider || null,
              workspaceId: meData.freshaWorkspaceId || get().workspaceId,
              isAuthenticated: true,
            });

            // Fetch remote global settings immediately upon auth
            useSettingsStore.getState().fetchRemoteSettings();

            // 2. Fetch Fresha workspace & business details if available
            if (meData.freshaConnected) {
              const sessionRes = await fetch('/api/auth/session', {
                headers: { Authorization: `Bearer ${token}` },
              });
              const sessionResult = await sessionRes.json().catch(() => ({}));
              if (sessionRes.ok && sessionResult.success && sessionResult.data) {
                const data = sessionResult.data;
                const locations: FreshaLocation[] = Array.isArray(data?.locations?.data)
                  ? data.locations.data
                  : Array.isArray(data?.locations)
                  ? data.locations
                  : [];
                const employees: FreshaEmployee[] = Array.isArray(data?.employees?.data)
                  ? data.employees.data
                  : Array.isArray(data?.employees)
                  ? data.employees
                  : [];
                const provider = data?.provider || null;
                const workspaceId = data?.workspaceId || get().workspaceId;

                const currentLocId = get().locationId;
                const defaultLocId =
                  currentLocId && locations.some((l) => l.id === currentLocId)
                    ? currentLocId
                    : locations[0]?.id || null;

                set({
                  workspaceId,
                  provider,
                  locations,
                  employees,
                  locationId: defaultLocId,
                  freshaConnected: true,
                });

                // Persist auth metadata to Dexie
                await setAppMeta<CachedAuthMeta>('auth_metadata', {
                  locations,
                  employees,
                  provider,
                  workspaceId,
                });
              }
            }
          } catch (err) {
            console.error('Failed to initialize session', err);
            // In catch/offline, keep cached locations & employees in state for offline navigation
          } finally {
            set({ isLoading: false });
          }
        },

        setLocationId: async (id: string | null) => {
          set({ locationId: id });
          const { workspaceId, token } = get();
          if (workspaceId && token && id && id !== 'all') {
            try {
              const res = await fetch(`/api/employees?locationId=${id}&workspaceId=${workspaceId}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const result = await res.json();
              const emps = Array.isArray(result?.data) ? result.data : [];
              if (emps.length > 0) {
                set({ employees: emps });
                const { locations, provider } = get();
                await setAppMeta<CachedAuthMeta>('auth_metadata', {
                  locations,
                  employees: emps,
                  provider,
                  workspaceId: get().workspaceId,
                });
              }
            } catch (e) {
              console.error('Error fetching employees for location', id, e);
            }
          }
        },
        setToken: (token: string | null) => set({ token, isAuthenticated: !!token }),
        setUser: (user: AuthUser | null) => set({ user }),
        setWorkspaceId: (workspaceId: string | null) => set({ workspaceId }),
        setLocations: (locations: FreshaLocation[]) => {
          set({ locations });
          const { employees, provider, workspaceId } = get();
          setAppMeta<CachedAuthMeta>('auth_metadata', {
            locations,
            employees,
            provider,
            workspaceId,
          }).catch(console.error);
        },
        setEmployees: (employees: FreshaEmployee[]) => {
          set({ employees });
          const { locations, provider, workspaceId } = get();
          setAppMeta<CachedAuthMeta>('auth_metadata', {
            locations,
            employees,
            provider,
            workspaceId,
          }).catch(console.error);
        },
        setProvider: (provider: FreshaProvider | null) => {
          set({ provider });
          const { locations, employees, workspaceId } = get();
          setAppMeta<CachedAuthMeta>('auth_metadata', {
            locations,
            employees,
            provider,
            workspaceId,
          }).catch(console.error);
        },
        setIsLoading: (isLoading: boolean) => set({ isLoading }),
        setClientVersion: (clientVersion: string) => set({ clientVersion }),
      }),
      {
        name: 'fresha-auth',
        partialize: (state) => ({
          token: state.token,
          user: state.user,
          workspaceId: state.workspaceId,
          locationId: state.locationId,
          isAuthenticated: state.isAuthenticated,
          clientVersion: state.clientVersion,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);
